import React from 'react';
import { Sparkle } from 'lucide-react';
import StatsBar from './StatsBar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FaYoutube, FaTwitch } from 'react-icons/fa';

// Kick SVG Component
const KickLogo = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" fillRule="evenodd" clipRule="evenodd" strokeLinejoin="round" strokeMiterlimit="2" fill="currentColor">
    <path d="M37 .036h164.448v113.621h54.71v-56.82h54.731V.036h164.448v170.777h-54.73v56.82h-54.711v56.8h54.71v56.82h54.73V512.03H310.89v-56.82h-54.73v-56.8h-54.711v113.62H37V.036z" />
  </svg>
);

// Upload SVG Component
const UploadLogo = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 13v8"/>
    <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"/>
    <path d="m8 17 4-4 4 4"/>
  </svg>
);

const HighlightedText = ({ children, color }: { children: React.ReactNode, color: string }) => (
  <span className={`inline-block rounded-lg px-1 py-1 sm:px-2 sm:py-1 md:px-3 lg:px-2 lg:py-2 mx-1 sm:mx-2 my-1 ${color} text-black font-semibold`}>
    {children}
  </span>
);

const HowItWorks = () => {
  return (
    <div className="relative w-full pt-5 text-center bg-transparent">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground max-w-5xl mx-auto">
          We
          <HighlightedText color="bg-green-500">automatically</HighlightedText>
          create
          <HighlightedText color="bg-blue-500">edited </HighlightedText>
          clips while you stream, for
          <HighlightedText color="bg-yellow-400">any content</HighlightedText>
        </h2>

        {/* Platform Labels */}
          <div className="flex flex-wrap justify-center items-center space-x-2 sm:space-x-3 md:space-x-4 mt-6 sm:mt-8 md:mt-10 mb-4 sm:mb-6 md:mb-8">
            {/* YouTube Icon + Text */}
            <span className="flex items-center justify-center space-x-1 sm:space-x-2 bg-red-500 border-2 border-white/20 text-white px-2 py-1 sm:px-3 md:px-4 sm:py-1 lg:py-2 rounded-full font-bold text-xs sm:text-sm md:text-lg lg:text-2xl select-none mb-2 sm:mb-0">
              <FaYoutube className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-7 lg:h-7" />
              <span>YOUTUBE</span>
            </span>
            {/* Twitch Icon + Text */}
            <span className="flex items-center justify-center space-x-1 sm:space-x-2 bg-purple-600 border-2 border-white/20 text-white px-2 py-1 sm:px-3 md:px-4 sm:py-1 lg:py-2 rounded-full font-bold text-xs sm:text-sm md:text-lg lg:text-2xl select-none mb-2 sm:mb-0">
              <FaTwitch className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-7 lg:h-7" />
              <span>TWITCH</span>
            </span>
            {/* Kick Icon + Text with Tooltip */}
            <span
              className="flex items-center justify-center space-x-1 sm:space-x-2 bg-[#53FC18] text-black px-2 py-1 sm:px-3 md:px-4 sm:py-1 lg:py-2 rounded-full font-bold text-xs sm:text-sm md:text-lg lg:text-2xl border-2 border-black/20 select-none mb-2 sm:mb-0"
            >
              <KickLogo className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-7 lg:h-7" />
              <span>KICK</span>
            </span>
            <span
              className="flex items-center justify-center space-x-1 sm:space-x-2 bg-black dark:bg-white text-white dark:text-black px-2 py-1 sm:px-3 md:px-4 sm:py-1 lg:py-2 rounded-full font-bold text-xs sm:text-sm md:text-lg lg:text-2xl border-2 border-white/20 dark:border-black/20 select-none mb-2 sm:mb-0"
            >
              <UploadLogo className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-7 lg:h-7" />
              <span>UPLOAD</span>
            </span>
          </div>
      </div>
    </div>
  );
};

export default HowItWorks;